import {ThemeOptions} from '@mui/material';
import {CSSProperties} from 'react';

import {Tokens} from '../../../../types/generated/Tokens';

export const MuiChip = ({chip, filterchip, focus}: Tokens): ThemeOptions['components'] => {
  // In MUI, this is the outlined variant
  const defaultStyle = {
    backgroundColor: filterchip?.closeable.desktop.background.color,
    borderColor: filterchip?.closeable.desktop.border.color,
    color: filterchip?.closeable.desktop.font.color,

    '& .MuiChip-icon': {
      color: filterchip?.closeable.desktop.icon.background.color
    },

    '& .MuiChip-deleteIcon': {
      color: filterchip?.closeable.desktop.icon.background.color
    },

    '.MuiChip-clickable&:focus': {
      backgroundColor: '#FFF'
    },

    '.MuiChip-clickable&:hover': {
      backgroundColor: filterchip.closeable.desktop.hover.background?.color,
      color: filterchip.closeable.desktop.hover.font.color,

      '& .MuiChip-icon, & .MuiChip-deleteIcon': {
        color: filterchip.closeable.desktop.hover.icon.background.color
      }
    },

    '.MuiChip-clickable&:active': {
      backgroundColor: filterchip.closeable.desktop.active?.background.color,
      borderColor: filterchip.closeable.desktop.active?.border.color,
      color: filterchip.closeable.desktop.active?.font.color,

      '& .MuiChip-icon, & .MuiChip-deleteIcon': {
        color: filterchip.closeable.desktop.active?.icon.background.color
      }
    }
  };

  // In MUI, this is the default variant
  const selectedStyle = {
    backgroundColor: filterchip?.selectable?.desktop.selected.background.color,
    borderColor: filterchip?.selectable?.desktop.selected.border.color,
    color: filterchip?.selectable?.desktop.selected.font.color,
    fontWeight: chip.filter.desktop.lg.font.weight as CSSProperties['fontWeight'],

    '& .MuiChip-icon': {
      color: filterchip?.selectable?.desktop.selected.icon.background.color
    },

    '& .MuiChip-deleteIcon': {
      backgroundColor: 'transparent',
      color: filterchip?.selectable?.desktop.selected.icon.background.color,

      '&:hover': {
        color: filterchip?.selectable?.desktop.selected.icon.background.color
      }
    },

    '&.MuiChip-deletable:focus': {
      backgroundColor: filterchip?.selectable?.desktop.selected.background.color,
      borderColor: filterchip?.selectable?.desktop.selected.border.color
    },

    '&.Mui-focusVisible': {
      backgroundColor: filterchip?.selectable?.desktop.selected.background.color,
      outlineOffset: focus.outline.width,
      outlineWidth: focus.outline.width,
      outlineStyle: focus.outline.style,
      outlineColor: focus.outline.color
    },

    '&:hover, &.MuiChip-deletable:hover': {
      backgroundColor: filterchip?.selectable?.desktop.selected.hover.background.color,
      borderColor: filterchip?.selectable?.desktop.selected.hover.border.color
    },

    '&:active, &.MuiChip-deletable:active': {
      color: filterchip.selectable.desktop.active?.font.color,
      backgroundColor: filterchip.selectable.desktop.active?.background.color,
      borderColor: filterchip.selectable.desktop.active?.border.color,
      boxShadow: 'none'
    }
  };

  // These are variants not provided by MUI but required by design
  const customChipStyle = {
    color: filterchip?.descriptiveText?.desktop.font.color,
    fontWeight: chip.filter.desktop.lg.font.weight,

    '& .MuiChip-icon': {
      color: filterchip?.descriptiveText?.desktop.font.color
    },

    '& .MuiChip-icon:hover': {
      color: filterchip?.descriptiveText?.desktop.font.color
    }
  };

  return {
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFF',
          border: `${chip.outlined.desktop.sm.border.width} ${chip.outlined.desktop.sm.border.style} ${chip.outlined.desktop.sm.border.color}`,
          borderBottomLeftRadius: chip.filter.desktop.sm.border.radius.bottomLeft,
          borderBottomRightRadius: chip.filter.desktop.sm.border.radius.bottomRight,
          borderTopLeftRadius: chip.filter.desktop.sm.border.radius.topLeft,
          borderTopRightRadius: chip.filter.desktop.sm.border.radius.topRight,
          color: filterchip?.descriptiveText?.desktop.font.color,
          fontSize: chip.filter.desktop.lg.font.size,

          '& .MuiChip-icon': {
            height: chip.badge.desktop.lg.icon.height,
            width: chip.badge.desktop.lg.icon.width
          },

          '& .MuiChip-deleteIcon': {
            height: chip.badge.desktop.lg.icon.height,
            width: chip.badge.desktop.lg.icon.width
          },

          '& .MuiChip-iconSmall': {
            height: chip.badge.desktop.sm.icon.height,
            width: chip.badge.desktop.sm.icon.width
          },

          '& .MuiChip-deleteIconSmall': {
            height: chip.badge.desktop.sm.icon.height,
            width: chip.badge.desktop.sm.icon.width
          },

          '& .MuiChip-avatarColorPrimary': {},

          '& .MuiChip-avatarColorSecondary': {},

          '&.MuiChip-customAlert': {
            ...customChipStyle,
            backgroundColor: chip.filled.desktop.sm.alert.background.color,
            borderColor: chip.filled.desktop.sm.alert.background.color,
            color: chip.filled.desktop.sm.alert.border.color,
            '& .MuiChip-icon, & .MuiChip-icon:hover': {
              color: chip.filled.desktop.sm.alert.border.color
            }
          },

          '&.MuiChip-customSuccess': {
            ...customChipStyle,
            backgroundColor: chip.filled.desktop.sm.success.background.color,
            borderColor: chip.filled.desktop.sm.success.background.color,
            color: chip.filled.desktop.sm.success.border.color,
            '& .MuiChip-icon, & .MuiChip-icon:hover': {
              color: chip.filled.desktop.sm.success.border.color
            }
          },

          '&.MuiChip-customInfo': {
            ...customChipStyle,
            backgroundColor: chip.filled.desktop.sm.info.background.color,
            borderColor: chip.filled.desktop.sm.info.background.color,
            color: chip.filled.desktop.sm.info.border.color,
            '& .MuiChip-icon, & .MuiChip-icon:hover': {
              color: chip.filled.desktop.sm.info.border.color
            }
          },

          '&.MuiChip-customWarning': {
            ...customChipStyle,
            backgroundColor: chip.filled.desktop.sm.warning.background.color,
            borderColor: chip.filled.desktop.sm.warning.background.color,
            color: chip.filled.desktop.sm.warning.font?.color,
            '& .MuiChip-icon, & .MuiChip-icon:hover': {
              color: chip.filled.desktop.sm.warning.icon?.color
            }
          },

          '&.MuiChip-customNeutral': {
            ...customChipStyle,
            backgroundColor: chip.filled.desktop.sm.neutral?.background.color,
            borderColor: chip.filled.desktop.sm.neutral?.background.color
          },

          '&.MuiChip-customTransparent': {
            ...customChipStyle,
            backgroundColor: 'transparent',
            borderColor: chip.filled.desktop.sm.border.color,
            '.MuiChip-label:only-child': {
              paddingLeft: 0,
              paddingRight: 0
            },

            '& .MuiChip-icon, & .MuiChip-icon:hover': {
              color: chip.badge.desktop.sm.icon.color
            }
          },

          '&.MuiChip-customOutlined': {
            ...customChipStyle
          }
        },

        sizeSmall: {
          fontSize: chip.badge.desktop.sm.font.size
        },

        colorPrimary: {},

        colorSecondary: {},

        disabled: {},

        clickable: selectedStyle,

        clickableColorPrimary: selectedStyle,

        clickableColorSecondary: selectedStyle,

        deletable: {
          '&:focus': {}
        },

        deletableColorPrimary: {
          '&:focus': {}
        },

        deletableColorSecondary: {
          '&:focus': {}
        },

        outlined: defaultStyle,

        outlinedPrimary: defaultStyle,

        outlinedSecondary: defaultStyle,

        label: {},

        labelSmall: {}
      }
    }
  };
};
